import { useTranslation } from 'react-i18next'

import type { DisplayFormat } from '../../types'

interface SummaryMetrics {
  totalTrackedDays: number
  totalTrackedHours: number
  totalBillableDays: number
  totalBillableHours: number
  totalNonBillableDays: number
  totalNonBillableHours: number
  avgUtilization: number
}

interface MyTeamSummaryCardsProps {
  metrics: SummaryMetrics
  displayFormat: DisplayFormat
  employeeCount: number
  dateLabel: string
}

const UTIL_TARGET = 70

const utilStatus = (util: number, t: (key: string) => string) =>
  util >= UTIL_TARGET
    ? {
        label: t('timeTracking.billing.statusOnTrack'),
        className: 'my-team-card--ok',
      }
    : {
        label: t('timeTracking.billing.statusLow'),
        className: 'my-team-card--low',
      }

export const MyTeamSummaryCards = ({
  metrics,
  displayFormat,
  employeeCount,
  dateLabel,
}: MyTeamSummaryCardsProps) => {
  const { t } = useTranslation()
  const isDays = displayFormat === 'days'

  const billablePercent =
    metrics.totalTrackedHours > 0
      ? Math.round(
          (metrics.totalBillableHours / metrics.totalTrackedHours) * 100,
        )
      : 0
  const nonBillablePercent =
    metrics.totalTrackedHours > 0
      ? Math.round(
          (metrics.totalNonBillableHours / metrics.totalTrackedHours) * 100,
        )
      : 0

  const status = utilStatus(metrics.avgUtilization, t)

  return (
    <div className="my-team-summary-cards">
      <div className="my-team-summary-card">
        <div className="my-team-summary-label">
          {t('timeTracking.billing.tracked')}
        </div>
        <div className="my-team-summary-value">
          {isDays
            ? `${metrics.totalTrackedDays}d`
            : `${metrics.totalTrackedHours}h`}
        </div>
        <div className="my-team-summary-sub">
          {t('timeTracking.billing.employees', { count: employeeCount })} ·{' '}
          {dateLabel}
        </div>
      </div>

      <div className="my-team-summary-card">
        <div className="my-team-summary-label">
          {t('timeTracking.billing.billable')}
        </div>
        <div className="my-team-summary-value">
          {isDays
            ? `${metrics.totalBillableDays}d`
            : `${metrics.totalBillableHours}h`}
        </div>
        <div className="my-team-summary-sub">
          {t('timeTracking.billing.percentOfTracked', {
            percent: billablePercent,
          })}
        </div>
        <div className="my-team-summary-bar">
          <div
            className="my-team-summary-bar-fill my-team-summary-bar-fill--green"
            style={{ width: `${billablePercent}%` }}
          />
        </div>
      </div>

      <div className="my-team-summary-card">
        <div className="my-team-summary-label">
          {t('timeTracking.billing.nonBillable')}
        </div>
        <div
          className={`my-team-summary-value${metrics.totalNonBillableHours === 0 ? ' my-team-summary-value--muted' : ''}`}
        >
          {isDays
            ? `${metrics.totalNonBillableDays}d`
            : `${metrics.totalNonBillableHours}h`}
        </div>
        <div className="my-team-summary-sub">
          {t('timeTracking.billing.percentOfTracked', {
            percent: nonBillablePercent,
          })}
        </div>
      </div>

      <div className={`my-team-summary-card ${status.className}`}>
        <div className="my-team-summary-label">
          {t('timeTracking.billing.avgUtilization')}
          <span className={`my-team-summary-badge ${status.className}`}>
            {status.label}
          </span>
        </div>
        <div className="my-team-summary-value">{metrics.avgUtilization}%</div>
        <div className="my-team-summary-sub">
          {t('timeTracking.billing.targetRange')}
        </div>
        <div className="my-team-summary-bar">
          <div
            className={`my-team-summary-bar-fill ${metrics.avgUtilization >= UTIL_TARGET ? 'my-team-summary-bar-fill--green' : 'my-team-summary-bar-fill--red'}`}
            style={{ width: `${Math.min(metrics.avgUtilization, 100)}%` }}
          />
        </div>
      </div>
    </div>
  )
}
